import { useEffect, useMemo, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/src/renderer/components/ui/popover'
import { onAppEvent } from '@/src/renderer/lib/events'
import { DISCOVER_ANCHOR, type DiscoverStepId } from '@/src/renderer/lib/intro'
import { IntroTip } from '../IntroTip'
import './DiscoverTip.scss'

/** So lange darf das Ziel nach dem Sprung brauchen, bis es steht (Seitenwechsel, Projekt lädt). */
const WAIT_MS = 1000
/** Steht das Ziel nicht mehr (Tab gewechselt, Seite zu), geht die Erklärung mit. */
const WATCH_MS = 400

/** Das erste sichtbare Ziel — Zeilen in verborgenen Tabs (`display: none`) haben keine Größe. */
export function findIntroAnchor(name: string, root: ParentNode = document): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>(`[data-intro-anchor="${name}"]`)) {
    const rect = element.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return element
  }
  return null
}

function isShown(element: HTMLElement): boolean {
  if (!element.isConnected) return false
  const rect = element.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

/**
 * **Entdecken › Punkt** (#323): die Erklärung an der Stelle, an die der Punkt geführt hat — derselbe große
 * Tooltip wie die Seitenhilfe, dazu ein pulsierender Ring am Ziel. Einmal im `MainLayout`; die Seitenleiste
 * meldet sich per `intro-discover`.
 *
 * Das Ziel ist ein fremdes Element (Titelleiste, Projektseite, Zeile) — daher `PopoverAnchor` mit
 * `virtualRef` statt eines Auslösers. Findet sich keins, steht die Erklärung am Punkt in „Entdecken“.
 */
export function DiscoverTip() {
  const [target, setTarget] = useState<{ step: DiscoverStepId; element: HTMLElement } | null>(null)
  const cancelSearch = useRef<() => void>(() => {})

  useEffect(() => {
    const unsubscribe = onAppEvent('intro-discover', ({ step, fallback }) => {
      cancelSearch.current()
      setTarget(null)
      let frame = 0
      const started = performance.now()
      const look = () => {
        const element = findIntroAnchor(DISCOVER_ANCHOR[step].anchor)
        if (element) { setTarget({ step, element }); return }
        if (performance.now() - started > WAIT_MS) {
          if (fallback && isShown(fallback)) setTarget({ step, element: fallback })
          return
        }
        frame = requestAnimationFrame(look)
      }
      // Erst nach dem nächsten Bild: Schaltet der Sprung auf einen schon offenen Tab um, steht im Klick noch der
      // alte da — dessen Zeile verschwände gleich mit ihm (display: none), und die Erklärung mit ihr.
      frame = requestAnimationFrame(() => { frame = requestAnimationFrame(look) })
      cancelSearch.current = () => cancelAnimationFrame(frame)
    })
    return () => {
      unsubscribe()
      cancelSearch.current()
    }
  }, [])

  // Der Ring am Ziel: ein Merkmal am fremden Element, die Optik steht in DiscoverTip.scss.
  useEffect(() => {
    if (!target) return
    const { element } = target
    element.setAttribute('data-intro-pulse', '')
    const watch = window.setInterval(() => { if (!isShown(element)) setTarget(null) }, WATCH_MS)
    return () => {
      window.clearInterval(watch)
      element.removeAttribute('data-intro-pulse')
    }
  }, [target])

  const anchorRef = useMemo(() => ({ current: target?.element ?? null }), [target])

  return (
    <Popover open={target != null} onOpenChange={open => { if (!open) setTarget(null) }}>
      {target && <PopoverAnchor virtualRef={anchorRef as { current: HTMLElement }} />}
      {target && (
        <PopoverContent
          side={DISCOVER_ANCHOR[target.step].side}
          align="center"
          sideOffset={10}
          collisionPadding={12}
          hideWhenDetached
          className="discover-tip"
          // Das Ziel ist zum Ausprobieren da: Der Fokus bleibt, wo er war.
          onOpenAutoFocus={event => event.preventDefault()}
        >
          <IntroTip steps={[target.step]} onClose={() => setTarget(null)} />
          <PopoverArrow className="intro-tip__arrow" width={16} height={8} />
        </PopoverContent>
      )}
    </Popover>
  )
}
